// features/inventory/pages/TransfersPage.tsx
/**
 * Stock Transfers Page — Create and manage inter-branch stock transfers.
 *
 * Route: /inventory/transfers
 *
 * Features:
 * - Dual-panel layout: product selection (left) + transfer cart (right)
 * - Branch selector with visual direction indicator
 * - Product search with card/list view toggle
 * - Cart management: add/remove items, adjust quantities
 * - Stock availability validation
 * - Transfer history with expandable details
 * - Status timeline with visual steps
 * - Approve, receive, and cancel actions
 * - Navigate to bulk transfer
 * - Real-time search with debounce
 */

import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { Card, CardContent } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import {
    getTransfers,
    createTransfer,
    approveTransfer,
    receiveTransfer,
    cancelTransfer,
    getTransfer,
    TransferSummary,
    TransferDetail,
} from '../services/inventoryApi';
import { getBranches } from '@/features/settings/services/settingsApi';
import { getProducts, ProductData } from '@/features/products/services/productsApi';
import {
    ArrowLeft,
    Plus,
    Save,
    Trash2,
    ArrowRightLeft,
    Package,
    Store,
    Search,
    X,
    CheckCircle,
    ShoppingCart,
    RefreshCw,
    Clock,
    User,
    Upload,
    Info,
    AlertCircle,
    ChevronRight,
    LayoutGrid,
    List,
    Truck,
    Ban,
    Eye,
    History,
    TrendingUp,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';

// ============================================================
// TYPES
// ============================================================

type ViewMode = 'card' | 'table';
type TransferFilter = 'all' | 'pending' | 'approved' | 'in_transit' | 'received' | 'cancelled';

interface CartItem {
    id: string;
    productId: string;
    productName: string;
    variantId?: string;
    variantName?: string;
    quantity: string;
    availableStock: string;
}

// ============================================================
// CONSTANTS
// ============================================================

const NIL_UUID = '00000000-0000-0000-0000-000000000000';

const TRANSFER_FILTERS: Array<{ value: TransferFilter; label: string }> = [
    { value: 'all', label: 'All' },
    { value: 'pending', label: 'Pending' },
    { value: 'approved', label: 'Approved' },
    { value: 'in_transit', label: 'In Transit' },
    { value: 'received', label: 'Received' },
    { value: 'cancelled', label: 'Cancelled' },
];

const STATUS_COLORS: Record<string, string> = {
    pending: 'bg-amber-100 text-amber-700',
    approved: 'bg-blue-100 text-blue-700',
    in_transit: 'bg-purple-100 text-purple-700',
    received: 'bg-emerald-100 text-emerald-700',
    cancelled: 'bg-rose-100 text-rose-700',
};

const STATUS_ORDER = ['pending', 'approved', 'in_transit', 'received'];

// ============================================================
// SUB-COMPONENTS
// ============================================================

function StatusTimeline({ currentStatus }: { currentStatus: string }) {
    if (currentStatus === 'cancelled') {
        return (
            <div className="flex items-center justify-center gap-3 mb-5 flex-wrap">
                <div className="w-8 h-8 rounded-full bg-rose-100 text-rose-500 flex items-center justify-center text-xs font-bold">
                    <Ban className="w-4 h-4" />
                </div>
                <span className="text-xs font-medium text-rose-600">
                    Cancelled
                </span>
            </div>
        );
    }

    const currentIdx = STATUS_ORDER.indexOf(currentStatus);

    return (
        <div className="flex items-center justify-center gap-3 mb-5 flex-wrap">
            {STATUS_ORDER.map((status, idx) => {
                const isComplete = idx <= currentIdx;
                const isCurrent = idx === currentIdx;

                return (
                    <div key={status} className="flex items-center gap-2">
                        <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ delay: idx * 0.1 }}
                            className={clsx(
                                'w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all',
                                isComplete
                                    ? 'bg-purple-500 text-white shadow-md'
                                    : isCurrent
                                        ? 'bg-purple-100 text-purple-700 ring-2 ring-purple-300'
                                        : 'bg-slate-100 text-slate-400'
                            )}
                        >
                            {isComplete ? (
                                <CheckCircle className="w-4 h-4" />
                            ) : (
                                idx + 1
                            )}
                        </motion.div>
                        <span
                            className={clsx(
                                'text-xs capitalize font-medium',
                                isCurrent
                                    ? 'text-purple-700'
                                    : isComplete
                                        ? 'text-purple-600'
                                        : 'text-slate-400'
                            )}
                        >
                            {status.replace(/_/g, ' ')}
                        </span>
                        {idx < 3 && (
                            <div
                                className={clsx(
                                    'w-6 h-0.5 rounded-full',
                                    isComplete
                                        ? 'bg-purple-400'
                                        : 'bg-slate-200'
                                )}
                            />
                        )}
                    </div>
                );
            })}
        </div>
    );
}

function TransferHistoryCard({
    transfer,
    isSelected,
    onSelect,
    onApprove,
    onReceive,
    onCancel,
    isApprovePending,
    isReceivePending,
    isCancelPending,
    getBranchName,
    formatDate,
}: {
    transfer: TransferSummary;
    isSelected: boolean;
    onSelect: () => void;
    onApprove: () => void;
    onReceive: () => void;
    onCancel: () => void;
    isApprovePending: boolean;
    isReceivePending: boolean;
    isCancelPending: boolean;
    getBranchName: (id: string) => string;
    formatDate: (dateStr: string | undefined) => string;
}) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            whileHover={{ y: -2 }}
        >
            <Card
                className="hover:shadow-md transition-shadow cursor-pointer overflow-hidden border-2 border-slate-200 hover:border-purple-200"
                onClick={onSelect}
            >
                <CardContent className="p-0">
                    <div className="p-4 flex items-center justify-between border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white">
                        <div className="flex items-center gap-3">
                            <span
                                className={clsx(
                                    'px-2 py-0.5 rounded-full text-[10px] font-medium',
                                    STATUS_COLORS[transfer.status] ||
                                    'bg-slate-100 text-slate-700'
                                )}
                            >
                                {transfer.status?.replace(/_/g, ' ')}
                            </span>
                            <div className="flex items-center gap-2 text-sm">
                                <span className="font-semibold">
                                    {transfer.fromBranchName ||
                                        getBranchName(transfer.fromBranchId)}
                                </span>
                                <motion.div
                                    animate={{ x: [0, 3, -3, 0] }}
                                    transition={{
                                        duration: 2,
                                        repeat: Infinity,
                                    }}
                                >
                                    <ArrowRightLeft className="w-4 h-4 text-purple-500" />
                                </motion.div>
                                <span className="font-semibold">
                                    {transfer.toBranchName ||
                                        getBranchName(transfer.toBranchId)}
                                </span>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <span className="text-xs text-slate-400">
                                {formatDate(transfer.createdAt)}
                            </span>
                            <span className="text-xs text-slate-500 font-medium">
                                {transfer.totalItems} items ·{' '}
                                {transfer.totalQuantity?.toLocaleString()} qty
                            </span>
                            <motion.div
                                animate={{
                                    rotate: isSelected ? 90 : 0,
                                }}
                                transition={{ duration: 0.2 }}
                            >
                                <ChevronRight className="w-4 h-4 text-slate-400" />
                            </motion.div>
                        </div>
                    </div>

                    <AnimatePresence>
                        {isSelected && (
                            <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                className="overflow-hidden"
                            >
                                <div className="p-5 border-t border-slate-100 bg-slate-50/30">
                                    <StatusTimeline
                                        currentStatus={transfer.status}
                                    />

                                    <div className="flex items-center gap-2 justify-center flex-wrap">
                                        {transfer.status === 'pending' && (
                                            <>
                                                <motion.button
                                                    whileHover={{
                                                        scale: 1.02,
                                                    }}
                                                    whileTap={{
                                                        scale: 0.98,
                                                    }}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        onApprove();
                                                    }}
                                                    disabled={isApprovePending}
                                                    className="flex items-center gap-1.5 px-4 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl text-sm font-medium hover:from-emerald-700 transition-all shadow-md"
                                                    type="button"
                                                >
                                                    {isApprovePending ? (
                                                        <Spinner
                                                            size="xs"
                                                            variant="white"
                                                        />
                                                    ) : (
                                                        <CheckCircle className="w-3.5 h-3.5" />
                                                    )}{' '}
                                                    Approve
                                                </motion.button>
                                                <motion.button
                                                    whileHover={{
                                                        scale: 1.02,
                                                    }}
                                                    whileTap={{
                                                        scale: 0.98,
                                                    }}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        onCancel();
                                                    }}
                                                    disabled={isCancelPending}
                                                    className="flex items-center gap-1.5 px-4 py-2 bg-white border-2 border-rose-200 text-rose-600 rounded-xl text-sm font-medium hover:bg-rose-50 transition-all"
                                                    type="button"
                                                >
                                                    {isCancelPending ? (
                                                        <Spinner
                                                            size="xs"
                                                            variant="white"
                                                        />
                                                    ) : (
                                                        <Ban className="w-3.5 h-3.5" />
                                                    )}{' '}
                                                    Cancel
                                                </motion.button>
                                            </>
                                        )}
                                        {(transfer.status === 'approved' ||
                                            transfer.status === 'in_transit') && (
                                                <motion.button
                                                    whileHover={{
                                                        scale: 1.02,
                                                    }}
                                                    whileTap={{
                                                        scale: 0.98,
                                                    }}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        onReceive();
                                                    }}
                                                    disabled={isReceivePending}
                                                    className="flex items-center gap-1.5 px-4 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl text-sm font-medium hover:from-emerald-700 transition-all shadow-md"
                                                    type="button"
                                                >
                                                    {isReceivePending ? (
                                                        <Spinner
                                                            size="xs"
                                                            variant="white"
                                                        />
                                                    ) : (
                                                        <Truck className="w-3.5 h-3.5" />
                                                    )}{' '}
                                                    Mark as Received
                                                </motion.button>
                                            )}
                                    </div>

                                    {transfer.requesterName && (
                                        <div className="mt-4 flex items-center gap-2 text-xs text-slate-400 justify-center">
                                            <User className="w-3 h-3" />
                                            Requested by{' '}
                                            {transfer.requesterName}
                                            {transfer.notes && (
                                                <span>
                                                    · "{transfer.notes}"
                                                </span>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </CardContent>
            </Card>
        </motion.div>
    );
}

// ============================================================
// MAIN COMPONENT
// ============================================================

export function TransfersPage() {
    const navigate = useNavigate();
    const user = useAuthStore((s) => s.user);
    const queryClient = useQueryClient();
    const addToast = useToastStore((s) => s.addToast);

    // State
    const [showHistory, setShowHistory] = useState(false);
    const [transferFilter, setTransferFilter] = useState<TransferFilter>('all');
    const [selectedTransferId, setSelectedTransferId] = useState<string | null>(null);
    const [fromBranchId, setFromBranchId] = useState('');
    const [toBranchId, setToBranchId] = useState('');
    const [transferNotes, setTransferNotes] = useState('');
    const [cartItems, setCartItems] = useState<CartItem[]>([]);
    const [productSearch, setProductSearch] = useState('');
    const [viewMode, setViewMode] = useState<ViewMode>('card');

    // Queries
    const { data: branches } = useQuery({
        queryKey: ['branches-transfer', user?.tenantId],
        queryFn: async () => {
            const data = await getBranches(user!.tenantId);
            return Array.isArray(data) ? data : [];
        },
        enabled: !!user?.tenantId,
    });

    const { data: products, isLoading: productsLoading } = useQuery<ProductData[]>({
        queryKey: ['products-transfer', user?.tenantId, productSearch],
        queryFn: async (): Promise<ProductData[]> => {
            const response = await getProducts(user!.tenantId, {
                limit: 100,
                search: productSearch || undefined,
                sortBy: 'name',
                order: 'asc',
            });
            return response?.data || [];
        },
        enabled: !!user?.tenantId,
    });

    const {
        data: transfers,
        isLoading: transfersLoading,
        refetch: refetchTransfers,
    } = useQuery({
        queryKey: ['inventory-transfers', user?.tenantId, transferFilter],
        queryFn: () =>
            getTransfers(user!.tenantId, {
                limit: 50,
                status: transferFilter === 'all' ? undefined : transferFilter,
            }),
        enabled: !!user?.tenantId && showHistory,
    });

    // Mutations
    const createTransferMutation = useMutation({
        mutationFn: () => {
            if (!fromBranchId || !toBranchId)
                throw new Error('Select both branches.');
            if (fromBranchId === toBranchId)
                throw new Error('Branches must be different.');
            if (cartItems.length === 0)
                throw new Error('Add at least one product.');
            return createTransfer(user!.tenantId, {
                fromBranchId,
                toBranchId,
                items: cartItems.map((i) => ({
                    productId: i.productId,
                    variantId: i.variantId,
                    quantity: i.quantity,
                })),
                notes: transferNotes || undefined,
            });
        },
        onSuccess: (transfer) => {
            addToast({
                type: 'success',
                title: 'Transfer created',
                message: `${cartItems.length} products queued.`,
            });
            setCartItems([]);
            setTransferNotes('');
            setShowHistory(true);
            setSelectedTransferId(transfer.id);
            queryClient.invalidateQueries({
                queryKey: ['inventory-transfers'],
            });
            queryClient.invalidateQueries({
                queryKey: ['transfers-dash'],
            });
        },
        onError: (err: any) =>
            addToast({
                type: 'error',
                title: 'Transfer failed',
                message: err?.message,
            }),
    });

    const approveMutation = useMutation({
        mutationFn: (transferId: string) =>
            approveTransfer(user!.tenantId, transferId),
        onSuccess: () => {
            addToast({ type: 'success', title: 'Transfer approved' });
            queryClient.invalidateQueries({
                queryKey: ['inventory-transfers'],
            });
            queryClient.invalidateQueries({
                queryKey: ['transfers-dash'],
            });
        },
        onError: (err: any) =>
            addToast({
                type: 'error',
                title: 'Approval failed',
                message: err?.message,
            }),
    });

    const receiveMutation = useMutation({
        mutationFn: (transferId: string) =>
            receiveTransfer(user!.tenantId, transferId),
        onSuccess: () => {
            addToast({
                type: 'success',
                title: 'Transfer received',
                message: 'Stock moved to destination.',
            });
            queryClient.invalidateQueries({
                queryKey: ['inventory-transfers'],
            });
            queryClient.invalidateQueries({
                queryKey: ['transfers-dash'],
            });
            queryClient.invalidateQueries({
                queryKey: ['inventory'],
            });
            queryClient.invalidateQueries({
                queryKey: ['stock-levels'],
            });
        },
        onError: (err: any) =>
            addToast({
                type: 'error',
                title: 'Receive failed',
                message: err?.message,
            }),
    });

    const cancelTransferMutation = useMutation({
        mutationFn: (transferId: string) =>
            cancelTransfer(user!.tenantId, transferId),
        onSuccess: () => {
            addToast({
                type: 'success',
                title: 'Transfer cancelled',
            });
            queryClient.invalidateQueries({
                queryKey: ['inventory-transfers'],
            });
            queryClient.invalidateQueries({
                queryKey: ['transfers-dash'],
            });
        },
        onError: (err: any) =>
            addToast({
                type: 'error',
                title: 'Cancel failed',
                message: err?.message,
            }),
    });

    // Derived data
    const branchesList = (branches || []) as Array<{
        id: string;
        name: string;
        code?: string;
    }>;
    const productsList: ProductData[] = products || [];
    const transfersList = (transfers || []) as TransferSummary[];
    const isSingleStore = branchesList.length <= 1;

    const getBranchName = useCallback(
        (id: string) =>
            branchesList.find((b: any) => b.id === id)?.name || 'Unknown',
        [branchesList]
    );

    const formatDate = (dateStr: string | undefined): string => {
        if (!dateStr) return '—';
        return new Date(dateStr).toLocaleDateString('en-NG', {
            day: 'numeric',
            month: 'short',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    const totalCartItems = cartItems.length;
    const totalCartQuantity = cartItems.reduce(
        (s, i) => s + (parseFloat(i.quantity) || 0),
        0
    );
    const canCreateTransfer =
        fromBranchId &&
        toBranchId &&
        fromBranchId !== toBranchId &&
        cartItems.length > 0;

    // Cart handlers
    const addToCart = useCallback(
        (product: ProductData) => {
            if (cartItems.some((i) => i.productId === product.id)) {
                addToast({
                    type: 'info',
                    title: 'Already in cart',
                    message: `${product.name} is already added.`,
                });
                return;
            }
            setCartItems([
                ...cartItems,
                {
                    id: `cart-${Date.now()}-${Math.random()
                        .toString(36)
                        .slice(2, 6)}`,
                    productId: product.id,
                    productName: product.name || '',
                    variantId: undefined,
                    variantName: undefined,
                    quantity: '1',
                    availableStock: '0',
                },
            ]);
        },
        [cartItems, addToast]
    );

    const removeFromCart = useCallback(
        (id: string) => setCartItems(cartItems.filter((i) => i.id !== id)),
        [cartItems]
    );

    const updateCartQuantity = useCallback(
        (id: string, quantity: string) =>
            setCartItems(
                cartItems.map((i) =>
                    i.id === id ? { ...i, quantity } : i
                )
            ),
        [cartItems]
    );

    // Single store mode
    if (isSingleStore) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/30">
                <div className="bg-white/90 backdrop-blur-md border-b border-slate-200 sticky top-0 z-30 shadow-sm">
                    <div className="max-w-[1600px] mx-auto px-6 lg:px-8 py-4">
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() => navigate('/inventory')}
                                className="p-2 rounded-xl hover:bg-slate-100"
                                type="button"
                            >
                                <ArrowLeft className="w-5 h-5 text-slate-600" />
                            </button>
                            <div className="p-2.5 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-2xl shadow-lg shadow-purple-500/25">
                                <ArrowRightLeft className="w-5 h-5 text-white" />
                            </div>
                            <div>
                                <h1 className="text-2xl font-bold text-slate-900">
                                    Stock Transfers
                                </h1>
                                <p className="text-sm text-slate-500">
                                    Single Store Mode
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="max-w-[1600px] mx-auto p-6 lg:p-8">
                    <EmptyState
                        icon={<ArrowRightLeft className="w-16 h-16" />}
                        title="Transfers Unavailable"
                        description="Stock transfers require at least 2 branches."
                        action={{
                            label: 'Configure Branches',
                            onClick: () => navigate('/settings'),
                        }}
                    />
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/30 flex flex-col">
            {/* Header */}
            <div className="bg-white/90 backdrop-blur-md border-b border-slate-200 sticky top-0 z-30 shadow-sm flex-shrink-0">
                <div className="max-w-[1600px] mx-auto px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() => navigate('/inventory')}
                                className="p-2 rounded-xl hover:bg-slate-100 transition-colors"
                                type="button"
                            >
                                <ArrowLeft className="w-5 h-5 text-slate-600" />
                            </button>
                            <motion.div
                                whileHover={{ rotate: -5 }}
                                className="p-2.5 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-2xl shadow-lg shadow-purple-500/25"
                            >
                                <ArrowRightLeft className="w-5 h-5 text-white" />
                            </motion.div>
                            <div>
                                <h1 className="text-2xl font-bold text-slate-900">
                                    Stock Transfers
                                </h1>
                                <p className="text-sm text-slate-500">
                                    {branchesList.length} branches ·{' '}
                                    {showHistory
                                        ? `${transfersList.length} transfers`
                                        : `${totalCartItems} in cart`}
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => refetchTransfers()}
                                disabled={transfersLoading}
                                className="flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition-all text-sm font-medium text-slate-700 shadow-sm"
                                type="button"
                            >
                                <RefreshCw
                                    className={clsx(
                                        'w-4 h-4',
                                        transfersLoading && 'animate-spin'
                                    )}
                                />{' '}
                                Refresh
                            </button>
                            <button
                                onClick={() =>
                                    navigate(
                                        '/inventory/transfers/bulk'
                                    )
                                }
                                className="flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition-all text-sm font-medium text-slate-700 shadow-sm"
                                type="button"
                            >
                                <Upload className="w-4 h-4" /> Bulk
                            </button>
                            <motion.button
                                whileHover={{ scale: 1.03 }}
                                whileTap={{ scale: 0.97 }}
                                onClick={() => {
                                    setShowHistory(!showHistory);
                                    setSelectedTransferId(null);
                                }}
                                className={clsx(
                                    'flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all shadow-sm',
                                    showHistory
                                        ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white'
                                        : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50'
                                )}
                                type="button"
                            >
                                <History className="w-4 h-4" />
                                {showHistory
                                    ? 'New Transfer'
                                    : `History (${transfersList.length})`}
                            </motion.button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="flex-1 overflow-hidden">
                {showHistory ? (
                    /* TRANSFER HISTORY VIEW */
                    <div className="max-w-[1600px] mx-auto p-6 lg:p-8 h-full overflow-y-auto space-y-4">
                        {/* Filter Pills */}
                        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-1.5">
                            <div className="flex items-center gap-1 bg-slate-100 rounded-xl p-0.5 flex-wrap">
                                {TRANSFER_FILTERS.map((f) => (
                                    <button
                                        key={f.value}
                                        onClick={() =>
                                            setTransferFilter(f.value)
                                        }
                                        className={clsx(
                                            'px-3 py-1.5 rounded-lg text-xs font-medium transition-all',
                                            transferFilter === f.value
                                                ? 'bg-white text-purple-600 shadow-sm'
                                                : 'text-slate-500 hover:text-slate-700'
                                        )}
                                        type="button"
                                    >
                                        {f.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Transfer List */}
                        {transfersLoading ? (
                            <div className="flex justify-center py-20">
                                <Spinner size="xl" />
                            </div>
                        ) : transfersList.length === 0 ? (
                            <EmptyState
                                icon={<ArrowRightLeft className="w-16 h-16" />}
                                title="No transfers found"
                                description={
                                    transferFilter !== 'all'
                                        ? 'No transfers match the selected filter.'
                                        : 'Create your first stock transfer to move inventory between branches.'
                                }
                                action={
                                    transferFilter === 'all'
                                        ? {
                                            label: 'Create Transfer',
                                            onClick: () =>
                                                setShowHistory(false),
                                        }
                                        : undefined
                                }
                            />
                        ) : (
                            <div className="space-y-3">
                                {transfersList.map((transfer) => (
                                    <TransferHistoryCard
                                        key={transfer.id}
                                        transfer={transfer}
                                        isSelected={
                                            selectedTransferId ===
                                            transfer.id
                                        }
                                        onSelect={() =>
                                            setSelectedTransferId(
                                                selectedTransferId ===
                                                    transfer.id
                                                    ? null
                                                    : transfer.id
                                            )
                                        }
                                        onApprove={() =>
                                            approveMutation.mutate(
                                                transfer.id
                                            )
                                        }
                                        onReceive={() =>
                                            receiveMutation.mutate(
                                                transfer.id
                                            )
                                        }
                                        onCancel={() =>
                                            cancelTransferMutation.mutate(
                                                transfer.id
                                            )
                                        }
                                        isApprovePending={
                                            approveMutation.isPending
                                        }
                                        isReceivePending={
                                            receiveMutation.isPending
                                        }
                                        isCancelPending={
                                            cancelTransferMutation.isPending
                                        }
                                        getBranchName={getBranchName}
                                        formatDate={formatDate}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                ) : (
                    /* NEW TRANSFER VIEW */
                    <div className="flex-1 flex flex-col lg:flex-row gap-4 p-6 lg:p-8 overflow-hidden max-w-[1600px] mx-auto w-full">
                        {/* Product Panel */}
                        <div className="flex-1 flex flex-col overflow-hidden bg-white rounded-2xl border border-slate-200 shadow-sm">
                            {/* Branch Selection */}
                            <div className="p-4 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-white">
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">
                                            From Branch *
                                        </label>
                                        <select
                                            value={fromBranchId}
                                            onChange={(e) =>
                                                setFromBranchId(
                                                    e.target.value
                                                )
                                            }
                                            className="w-full px-3 py-2.5 border-2 border-slate-200 rounded-xl text-sm bg-white focus:ring-2 focus:ring-purple-500/20 focus:border-purple-400 transition-all"
                                        >
                                            <option value="">
                                                Select source...
                                            </option>
                                            {branchesList.map(
                                                (b: any) => (
                                                    <option
                                                        key={b.id}
                                                        value={b.id}
                                                        disabled={
                                                            b.id ===
                                                            toBranchId
                                                        }
                                                    >
                                                        {b.name}
                                                    </option>
                                                )
                                            )}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">
                                            To Branch *
                                        </label>
                                        <select
                                            value={toBranchId}
                                            onChange={(e) =>
                                                setToBranchId(
                                                    e.target.value
                                                )
                                            }
                                            className="w-full px-3 py-2.5 border-2 border-slate-200 rounded-xl text-sm bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 transition-all"
                                        >
                                            <option value="">
                                                Select destination...
                                            </option>
                                            {branchesList.map(
                                                (b: any) => (
                                                    <option
                                                        key={b.id}
                                                        value={b.id}
                                                        disabled={
                                                            b.id ===
                                                            fromBranchId
                                                        }
                                                    >
                                                        {b.name}
                                                    </option>
                                                )
                                            )}
                                        </select>
                                    </div>
                                </div>
                                {fromBranchId &&
                                    toBranchId &&
                                    fromBranchId !== toBranchId && (
                                        <motion.div
                                            initial={{
                                                opacity: 0,
                                                y: -5,
                                            }}
                                            animate={{
                                                opacity: 1,
                                                y: 0,
                                            }}
                                            className="mt-3 p-2.5 bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-lg flex items-center justify-center gap-3 text-sm"
                                        >
                                            <span className="font-semibold text-purple-700">
                                                {getBranchName(
                                                    fromBranchId
                                                )}
                                            </span>
                                            <TrendingUp className="w-4 h-4 text-purple-400" />
                                            <span className="font-semibold text-indigo-700">
                                                {getBranchName(
                                                    toBranchId
                                                )}
                                            </span>
                                        </motion.div>
                                    )}
                            </div>

                            {/* Product Search */}
                            <div className="p-3 border-b flex items-center gap-2">
                                <div className="relative flex-1">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                    <input
                                        type="text"
                                        placeholder="Search products or scan barcode..."
                                        value={productSearch}
                                        onChange={(e) =>
                                            setProductSearch(
                                                e.target.value
                                            )
                                        }
                                        className="w-full pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-purple-500/20 focus:border-purple-400 transition-all"
                                    />
                                </div>
                                <div className="flex items-center bg-slate-100 rounded-lg p-0.5">
                                    <button
                                        onClick={() =>
                                            setViewMode('card')
                                        }
                                        className={clsx(
                                            'p-1.5 rounded-md transition-all',
                                            viewMode === 'card'
                                                ? 'bg-white shadow-sm text-purple-600'
                                                : 'text-slate-400'
                                        )}
                                        type="button"
                                    >
                                        <LayoutGrid className="w-4 h-4" />
                                    </button>
                                    <button
                                        onClick={() =>
                                            setViewMode('table')
                                        }
                                        className={clsx(
                                            'p-1.5 rounded-md transition-all',
                                            viewMode === 'table'
                                                ? 'bg-white shadow-sm text-purple-600'
                                                : 'text-slate-400'
                                        )}
                                        type="button"
                                    >
                                        <List className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>

                            {/* Product List */}
                            <div className="flex-1 overflow-y-auto p-3">
                                {productsLoading ? (
                                    <div className="flex justify-center py-12">
                                        <Spinner size="lg" />
                                    </div>
                                ) : productsList.length === 0 ? (
                                    <motion.div
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        className="flex flex-col items-center justify-center py-12 text-slate-400"
                                    >
                                        <motion.div
                                            animate={{
                                                y: [0, -6, 0],
                                            }}
                                            transition={{
                                                duration: 2,
                                                repeat: Infinity,
                                            }}
                                            className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center mb-3"
                                        >
                                            <Package className="w-6 h-6" />
                                        </motion.div>
                                        <p className="text-sm">
                                            No products found
                                        </p>
                                    </motion.div>
                                ) : viewMode === 'card' ? (
                                    <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2">
                                        {productsList.map(
                                            (product, idx) => {
                                                const isInCart =
                                                    cartItems.some(
                                                        (i) =>
                                                            i.productId ===
                                                            product.id
                                                    );
                                                return (
                                                    <motion.button
                                                        key={
                                                            product.id
                                                        }
                                                        initial={{
                                                            opacity: 0,
                                                            y: 10,
                                                        }}
                                                        animate={{
                                                            opacity: 1,
                                                            y: 0,
                                                        }}
                                                        transition={{
                                                            delay:
                                                                idx *
                                                                0.02,
                                                        }}
                                                        whileHover={{
                                                            scale: 1.02,
                                                        }}
                                                        whileTap={{
                                                            scale: 0.98,
                                                        }}
                                                        onClick={() =>
                                                            addToCart(
                                                                product
                                                            )
                                                        }
                                                        disabled={
                                                            isInCart
                                                        }
                                                        className={clsx(
                                                            'text-left p-3 rounded-xl border-2 transition-all',
                                                            isInCart
                                                                ? 'border-purple-300 bg-purple-50/50'
                                                                : 'border-slate-200 hover:border-purple-400 hover:shadow-md'
                                                        )}
                                                    >
                                                        <div className="flex items-start gap-2">
                                                            <motion.div
                                                                whileHover={{
                                                                    rotate: -5,
                                                                }}
                                                                className={clsx(
                                                                    'w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0',
                                                                    isInCart
                                                                        ? 'bg-purple-100'
                                                                        : 'bg-slate-100'
                                                                )}
                                                            >
                                                                <Package
                                                                    className={clsx(
                                                                        'w-5 h-5',
                                                                        isInCart
                                                                            ? 'text-purple-500'
                                                                            : 'text-slate-400'
                                                                    )}
                                                                />
                                                            </motion.div>
                                                            <div className="flex-1 min-w-0">
                                                                <p className="text-sm font-semibold truncate">
                                                                    {
                                                                        product.name
                                                                    }
                                                                </p>
                                                                <p className="text-xs text-slate-400 mt-0.5">
                                                                    {product.sku ||
                                                                        product.brand ||
                                                                        'No SKU'}
                                                                </p>
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-100">
                                                            <span className="text-xs text-slate-400">
                                                                Stock:{' '}
                                                                <span className="font-medium text-slate-600">
                                                                    —
                                                                </span>
                                                            </span>
                                                            {isInCart ? (
                                                                <CheckCircle className="w-4 h-4 text-purple-500" />
                                                            ) : (
                                                                <Plus className="w-4 h-4 text-purple-500" />
                                                            )}
                                                        </div>
                                                    </motion.button>
                                                );
                                            }
                                        )}
                                    </div>
                                ) : (
                                    <table className="min-w-full text-sm">
                                        <thead>
                                            <tr>
                                                <th className="text-left py-2 text-[10px] uppercase text-slate-400">
                                                    Product
                                                </th>
                                                <th className="text-right py-2 text-[10px] uppercase text-slate-400">
                                                    SKU
                                                </th>
                                                <th className="text-center py-2 w-16">
                                                    Add
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {productsList.map(
                                                (product) => {
                                                    const isInCart =
                                                        cartItems.some(
                                                            (i) =>
                                                                i.productId ===
                                                                product.id
                                                        );
                                                    return (
                                                        <tr
                                                            key={
                                                                product.id
                                                            }
                                                            className="border-b border-slate-50 hover:bg-slate-50"
                                                        >
                                                            <td className="py-2 font-medium truncate max-w-[200px]">
                                                                {
                                                                    product.name
                                                                }
                                                            </td>
                                                            <td className="py-2 text-right text-xs text-slate-400">
                                                                {product.sku ||
                                                                    '—'}
                                                            </td>
                                                            <td className="py-2 text-center">
                                                                <button
                                                                    onClick={() =>
                                                                        addToCart(
                                                                            product
                                                                        )
                                                                    }
                                                                    disabled={
                                                                        isInCart
                                                                    }
                                                                    className={clsx(
                                                                        'p-1 rounded-lg',
                                                                        isInCart
                                                                            ? 'text-purple-500'
                                                                            : 'text-slate-400 hover:text-purple-500'
                                                                    )}
                                                                    type="button"
                                                                >
                                                                    {isInCart ? (
                                                                        <CheckCircle className="w-4 h-4" />
                                                                    ) : (
                                                                        <Plus className="w-4 h-4" />
                                                                    )}
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    );
                                                }
                                            )}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        </div>

                        {/* Cart Panel */}
                        <div className="lg:w-[420px] bg-white rounded-2xl border border-slate-200 shadow-sm flex flex-col overflow-hidden">
                            <div className="px-4 py-3 border-b bg-gradient-to-r from-purple-50 to-indigo-50 flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <motion.div
                                        whileHover={{ rotate: -5 }}
                                    >
                                        <ShoppingCart className="w-5 h-5 text-purple-600" />
                                    </motion.div>
                                    <h3 className="font-semibold text-sm">
                                        Transfer Cart
                                    </h3>
                                </div>
                                {totalCartItems > 0 && (
                                    <motion.div
                                        initial={{ scale: 0 }}
                                        animate={{ scale: 1 }}
                                        transition={{
                                            type: 'spring',
                                        }}
                                    >
                                        <Badge variant="neutral" size="sm">
                                            {totalCartItems} items ·{' '}
                                            {totalCartQuantity.toLocaleString()}{' '}
                                            qty
                                        </Badge>
                                    </motion.div>
                                )}
                            </div>

                            <div className="flex-1 overflow-y-auto p-3 space-y-2">
                                <AnimatePresence>
                                    {cartItems.length === 0 ? (
                                        <motion.div
                                            initial={{
                                                opacity: 0,
                                                y: 20,
                                            }}
                                            animate={{
                                                opacity: 1,
                                                y: 0,
                                            }}
                                            className="flex flex-col items-center justify-center h-full text-slate-400 py-8"
                                        >
                                            <motion.div
                                                animate={{
                                                    y: [0, -8, 0],
                                                    rotate: [
                                                        0, -3, 3,
                                                        0,
                                                    ],
                                                }}
                                                transition={{
                                                    duration: 3,
                                                    repeat: Infinity,
                                                }}
                                                className="mb-4"
                                            >
                                                <ShoppingCart
                                                    className="w-16 h-16 opacity-20"
                                                    strokeWidth={1}
                                                />
                                            </motion.div>
                                            <p className="text-sm font-medium">
                                                Your cart is empty
                                            </p>
                                            <p className="text-xs mt-1">
                                                Select products from
                                                the left panel
                                            </p>
                                        </motion.div>
                                    ) : (
                                        cartItems.map(
                                            (item, idx) => (
                                                <motion.div
                                                    key={item.id}
                                                    initial={{
                                                        opacity: 0,
                                                        x: 20,
                                                    }}
                                                    animate={{
                                                        opacity: 1,
                                                        x: 0,
                                                    }}
                                                    exit={{
                                                        opacity: 0,
                                                        x: -20,
                                                    }}
                                                    transition={{
                                                        delay:
                                                            idx *
                                                            0.05,
                                                    }}
                                                    className="p-3 bg-slate-50 rounded-xl border border-slate-100 hover:bg-white hover:shadow-sm transition-all"
                                                >
                                                    <div className="flex items-start justify-between mb-2">
                                                        <div className="flex items-center gap-2 flex-1 min-w-0">
                                                            <motion.div
                                                                whileHover={{
                                                                    rotate: -5,
                                                                }}
                                                                className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0"
                                                            >
                                                                <Package className="w-4 h-4 text-purple-500" />
                                                            </motion.div>
                                                            <div className="min-w-0">
                                                                <p className="text-sm font-medium truncate">
                                                                    {
                                                                        item.productName
                                                                    }
                                                                </p>
                                                            </div>
                                                        </div>
                                                        <motion.button
                                                            whileHover={{
                                                                scale: 1.1,
                                                            }}
                                                            whileTap={{
                                                                scale: 0.9,
                                                            }}
                                                            onClick={() =>
                                                                removeFromCart(
                                                                    item.id
                                                                )
                                                            }
                                                            className="p-1 text-slate-400 hover:text-rose-500 transition-colors"
                                                            type="button"
                                                        >
                                                            <Trash2 className="w-3.5 h-3.5" />
                                                        </motion.button>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <input
                                                            type="number"
                                                            step="0.001"
                                                            min="0"
                                                            value={
                                                                item.quantity
                                                            }
                                                            onChange={(
                                                                e
                                                            ) =>
                                                                updateCartQuantity(
                                                                    item.id,
                                                                    e
                                                                        .target
                                                                        .value
                                                                )
                                                            }
                                                            className="w-full px-3 py-2 border-2 border-slate-200 rounded-lg text-sm text-right focus:ring-2 focus:ring-purple-500/20 focus:border-purple-400 transition-all"
                                                        />
                                                        <span className="text-xs text-slate-400 w-12 text-right">
                                                            units
                                                        </span>
                                                    </div>
                                                </motion.div>
                                            )
                                        )
                                    )}
                                </AnimatePresence>
                            </div>

                            <div className="px-4 py-4 border-t bg-gradient-to-r from-slate-50 to-white space-y-3">
                                <div>
                                    <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">
                                        Notes
                                    </label>
                                    <textarea
                                        value={transferNotes}
                                        onChange={(e) =>
                                            setTransferNotes(
                                                e.target.value
                                            )
                                        }
                                        rows={2}
                                        className="w-full px-3 py-2 border-2 border-slate-200 rounded-xl text-sm resize-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-400 transition-all"
                                        placeholder="Reason for transfer (optional)..."
                                    />
                                </div>
                                <motion.button
                                    whileHover={{ scale: 1.01 }}
                                    whileTap={{ scale: 0.98 }}
                                    onClick={() =>
                                        createTransferMutation.mutate()
                                    }
                                    disabled={
                                        !canCreateTransfer ||
                                        createTransferMutation.isPending
                                    }
                                    className="flex items-center justify-center gap-2 w-full px-6 py-3 bg-gradient-to-r from-purple-500 to-indigo-600 text-white rounded-xl hover:from-purple-600 hover:to-indigo-700 transition-all font-bold shadow-lg shadow-purple-500/25 disabled:opacity-50 text-sm"
                                    type="button"
                                >
                                    {createTransferMutation.isPending ? (
                                        <Spinner
                                            size="xs"
                                            variant="white"
                                        />
                                    ) : (
                                        <Save className="w-4 h-4" />
                                    )}
                                    Create Transfer
                                </motion.button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}